import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';

const MarkAttendance = () => {
  // const { user } = useAuth(); // user not currently used
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isHod = storedUser?.role === 'HOD';
  const canExport = ['HOD', 'ADMIN', 'FACULTY'].includes(storedUser?.role);
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const [selectedDate, setSelectedDate] = useState(today);

  // Only allow editing attendance for today's date
  const isEditable = selectedDate === today;
  const [slotNumber, setSlotNumber] = useState('1');
  const [department, setDepartment] = useState('');
  const [semester, setSemester] = useState('');
  const [section, setSection] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [subjectName, setSubjectName] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [students, setStudents] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [showSubjectManager, setShowSubjectManager] = useState(false);
  const [subjectForm, setSubjectForm] = useState({ code: '', name: '' });
  const [subjectToRemove, setSubjectToRemove] = useState('');
  const [subjectActionLoading, setSubjectActionLoading] = useState(false);
  const [mobileAttendanceStarted, setMobileAttendanceStarted] = useState(false);
  const [mobileStudentIndex, setMobileStudentIndex] = useState(0);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [desktopAttendanceOpen, setDesktopAttendanceOpen] = useState(false);
  const [desktopStudentIndex, setDesktopStudentIndex] = useState(0);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  
  const departments = ['CS', 'CD', 'CI', 'CY', 'EC', 'ME'];
  const semesters = [
    { value: 'SEM_1', label: '1st Semester' },
    { value: 'SEM_2', label: '2nd Semester' },
    { value: 'SEM_3', label: '3rd Semester' },
    { value: 'SEM_4', label: '4th Semester' },
    { value: 'SEM_5', label: '5th Semester' },
    { value: 'SEM_6', label: '6th Semester' },
    { value: 'SEM_7', label: '7th Semester' },
    { value: 'SEM_8', label: '8th Semester' }
  ];
  const sections = ['A', 'B', 'C'];

  useEffect(() => {
    if (department && semester) {
      fetchSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department, semester]);

  useEffect(() => {
    if (department && semester) {
      loadSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, slotNumber, department, semester, section, subjectId]);

  useEffect(() => {
    if (mobileStudentIndex >= students.length) {
      setMobileStudentIndex(Math.max(students.length - 1, 0));
    }
  }, [mobileStudentIndex, students.length]);

  const filteredStudents = students.filter(record => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(record.student?.name || '').toLowerCase().includes(query) ||
      String(record.student?.usn || '').toLowerCase().includes(query) ||
      String(record.student?.collegeEmail || '').toLowerCase().includes(query)
    );
  });

  const desktopCurrentStudent = filteredStudents[desktopStudentIndex] || null;
  const desktopCurrentStatus = desktopCurrentStudent
    ? attendanceData[desktopCurrentStudent.studentId]?.status || desktopCurrentStudent.status || 'NOT_MARKED'
    : 'NOT_MARKED';
  const isDesktopLast = filteredStudents.length > 0 && desktopStudentIndex >= filteredStudents.length - 1;
  const showDesktopSubmit = isEditable && isDesktopLast;
  const isMobileLast = students.length > 0 && mobileStudentIndex >= students.length - 1;
  const showMobileSubmit = isEditable && isMobileLast;

  useEffect(() => {
    if (desktopStudentIndex >= filteredStudents.length) {
      setDesktopStudentIndex(Math.max(filteredStudents.length - 1, 0));
    }
  }, [desktopStudentIndex, filteredStudents.length]);

  const autoAdvanceDesktop = useCallback((delayMs = 180) => {
    const advance = () => {
      setDesktopStudentIndex((prev) => {
        if (prev >= filteredStudents.length - 1) return prev;
        return prev + 1;
      });
    };

    if (delayMs > 0) {
      setTimeout(advance, delayMs);
      return;
    }

    advance();
  }, [filteredStudents.length]);

  useEffect(() => {
    if (!desktopAttendanceOpen) return;

    const handleKeyDown = (event) => {
      const tagName = event.target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || event.target?.isContentEditable) {
        return;
      }

      const maxIndex = filteredStudents.length - 1;
      const hasNavModifier = isMac ? event.metaKey : event.ctrlKey;

      if (hasNavModifier && event.key === 'ArrowLeft') {
        event.preventDefault();
        setDesktopStudentIndex((prev) => Math.max(prev - 1, 0));
        return;
      }

      if (hasNavModifier && event.key === 'ArrowRight') {
        event.preventDefault();
        setDesktopStudentIndex((prev) => Math.min(prev + 1, maxIndex));
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeDesktopAttendance();
        return;
      }

      if (!isEditable || filteredStudents.length === 0) return;

      const current = filteredStudents[desktopStudentIndex];
      if (!current) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleStatusChange(current.studentId, 'ABSENT');
        autoAdvanceDesktop();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleStatusChange(current.studentId, 'PRESENT');
        autoAdvanceDesktop();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        handleStatusChange(current.studentId, 'LATE');
        autoAdvanceDesktop();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        handleStatusChange(current.studentId, 'EXCUSED');
        autoAdvanceDesktop();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [desktopAttendanceOpen, desktopStudentIndex, filteredStudents, isEditable, isMac, autoAdvanceDesktop]);

  const fetchSubjects = async () => {
    try {
      setLoadingSubjects(true);
      const response = await api.get(`/attendance/subjects?department=${department}&semester=${semester}`);
      setSubjects(response.data.data || []);
    } catch (error) {
      console.error('Fetch subjects error:', error);
      toast.error(error.response?.data?.error || 'Failed to load subjects');
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleAddSubject = async () => {
    if (!department || !semester) {
      toast.error('Please select department and semester first');
      return;
    }

    const trimmedCode = subjectForm.code.trim();
    const trimmedName = subjectForm.name.trim();

    if (!trimmedCode || !trimmedName) {
      toast.error('Subject code and name are required');
      return;
    }

    try {
      setSubjectActionLoading(true);
      await api.post('/attendance/subjects', {
        code: trimmedCode,
        name: trimmedName,
        faculty: storedUser?.name || 'Admin',
        credits: 0,
        category: 'CORE',
        department,
        semester
      });
      toast.success('Subject added');
      setSubjectForm({ code: '', name: '' });
      fetchSubjects();
    } catch (error) {
      console.error('Add subject error:', error);
      toast.error(error.response?.data?.error || 'Failed to add subject');
    } finally {
      setSubjectActionLoading(false);
    }
  };

  const handleRemoveSubject = async () => {
    if (!subjectToRemove) {
      toast.error('Please select a subject to remove');
      return;
    }

    try {
      setSubjectActionLoading(true);
      await api.delete(`/attendance/subjects/${subjectToRemove}`);
      toast.success('Subject removed');
      setSubjectToRemove('');
      fetchSubjects();
    } catch (error) {
      console.error('Remove subject error:', error);
      toast.error(error.response?.data?.error || 'Failed to remove subject');
    } finally {
      setSubjectActionLoading(false);
    }
  };

  const loadSession = async () => {
    try {
      setLoading(true);
      const response = await api.post('/attendance/session', {
        date: selectedDate,
        slotNumber: parseInt(slotNumber),
        department,
        semester,
        section: section || null,
        subjectId: subjectId ? parseInt(subjectId) : null
      });

      const session = response.data.data;
      setSessionId(session.id);
      setStudents(session.records || []);
      
      // Initialize attendance data from existing records
      const initialData = {};
      session.records.forEach(record => {
        initialData[record.studentId] = {
          status: record.status,
          remarks: record.remarks || ''
        };
      });
      setAttendanceData(initialData);
      
      if (session.isMarked) {
        setIsUpdateMode(true);
        if (isEditable) {
          toast.info('This session has already been marked. You can update the attendance.');
        }
      } else {
        setIsUpdateMode(false);
      }
      
      // Set subject name if session has subject and user hasn't selected one yet
      if (session.subject) {
        setSubjectName(session.subject.name);
        if (!subjectId) {
          setSubjectId(session.subject.id.toString());
        }
      }
    } catch (error) {
      console.error('Load session error:', error);
      toast.error('Failed to load attendance session');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        status
      }
    }));
  };

  const handleRemarksChange = (studentId, remarks) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        remarks
      }
    }));
  };

  const markAllPresent = () => {
    const newData = {};
    students.forEach(record => {
      newData[record.studentId] = {
        status: 'PRESENT',
        remarks: ''
      };
    });
    setAttendanceData(newData);
  };

  const markAllAbsent = () => {
    const newData = {};
    students.forEach(record => {
      newData[record.studentId] = {
        status: 'ABSENT',
        remarks: ''
      };
    });
    setAttendanceData(newData);
  };

  const startMobileAttendance = () => {
    setMobileAttendanceStarted(true);
    setMobileStudentIndex(0);
  };

  const openMobileStudent = (index) => {
    setMobileAttendanceStarted(true);
    setMobileStudentIndex(index);
  };

  const handleMobileBackToList = () => {
    setMobileAttendanceStarted(false);
  };

  const openDesktopAttendance = () => {
    if (filteredStudents.length === 0) return;
    setDesktopStudentIndex(0);
    setDesktopAttendanceOpen(true);
  };

  const openDesktopAttendanceAt = (index) => {
    if (filteredStudents.length === 0) return;
    setDesktopStudentIndex(index);
    setDesktopAttendanceOpen(true);
  };

  const closeDesktopAttendance = () => {
    setDesktopAttendanceOpen(false);
  };

  const goToDesktopPrev = () => {
    setDesktopStudentIndex((prev) => Math.max(prev - 1, 0));
  };

  const goToDesktopNext = () => {
    setDesktopStudentIndex((prev) => Math.min(prev + 1, filteredStudents.length - 1));
  };

  const mobileCurrentStudent = students[mobileStudentIndex] || null;
  const mobileCurrentStatus = mobileCurrentStudent
    ? attendanceData[mobileCurrentStudent.studentId]?.status || mobileCurrentStudent.status
    : 'NOT_MARKED';

  const handleSubmit = async () => {
    try {
      if (!sessionId) {
        toast.error('Please load a session first');
        return;
      }

      if (!subjectId) {
        toast.error('Please select a subject before submitting attendance');
        return;
      }

      // Validate: no student should be left as NOT_MARKED
      const unmarkedStudents = students.filter(record => {
        const status = attendanceData[record.studentId]?.status;
        return !status || status === 'NOT_MARKED';
      });

      if (unmarkedStudents.length > 0) {
        toast.error(
          `${unmarkedStudents.length} student${unmarkedStudents.length > 1 ? 's have' : ' has'} not been marked. Please mark all students before submitting.`,
          { autoClose: 4000 }
        );
        return;
      }

      setLoading(true);

      const dataToSubmit = students.map(record => ({
        studentId: record.studentId,
        status: attendanceData[record.studentId]?.status || 'NOT_MARKED',
        remarks: attendanceData[record.studentId]?.remarks || ''
      }));

      const response = await api.post('/attendance/mark', {
        sessionId,
        attendanceData: dataToSubmit
      });

      const result = response.data?.data;
      if (result && result.updatedRecords === 0) {
        toast.warn('Warning: No records were updated. Please try again.');
        console.warn('Attendance update returned 0 records updated:', result);
      } else {
        toast.success(`Attendance updated successfully! (${result?.updatedRecords || dataToSubmit.length} records)`);
        if (desktopAttendanceOpen) {
          closeDesktopAttendance();
        }
        if (mobileAttendanceStarted) {
          handleMobileBackToList();
        }
      }
      loadSession(); // Reload to show updated data
    } catch (error) {
      console.error('Mark attendance error:', error);
      toast.error('Failed to mark attendance');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PRESENT': return 'bg-green-100 border-green-500 text-green-800';
      case 'ABSENT': return 'bg-red-100 border-red-500 text-red-800';
      case 'LATE': return 'bg-yellow-100 border-yellow-500 text-yellow-800';
      case 'EXCUSED': return 'bg-blue-100 border-blue-500 text-blue-800';
      default: return 'bg-gray-100 border-gray-500 text-gray-800';
    }
  };

  const getAttendanceStats = () => {
    const stats = {
      present: 0,
      absent: 0,
      late: 0,
      excused: 0,
      notMarked: 0
    };

    students.forEach(record => {
      const status = attendanceData[record.studentId]?.status || record.status;
      switch (status) {
        case 'PRESENT': stats.present++; break;
        case 'ABSENT': stats.absent++; break;
        case 'LATE': stats.late++; break;
        case 'EXCUSED': stats.excused++; break;
        default: stats.notMarked++; break;
      }
    });

    return stats;
  };

  const stats = getAttendanceStats();

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Mark Attendance</h1>
        <p className="text-gray-600 mt-2">Mark attendance for your class</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Class Details</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <input
              type="date"
              value={selectedDate}
              max={today}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Slot (1-8)</label>
            <select
              value={slotNumber}
              onChange={(e) => setSlotNumber(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map(slot => (
                <option key={slot} value={slot}>Slot {slot}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester</label>
            <select
              value={semester}
              onChange={(e) => setSemester(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Semester</option>
              {semesters.map(sem => (
                <option key={sem.value} value={sem.value}>{sem.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section (Optional)</label>
            <select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sections</option>
              {sections.map(sec => (
                <option key={sec} value={sec}>{sec}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject <span className="text-red-500">*</span></label>
            <select
              value={subjectId}
              onChange={(e) => {
                setSubjectId(e.target.value);
                const selected = subjects.find(s => s.id.toString() === e.target.value);
                setSubjectName(selected ? selected.name : '');
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={loadingSubjects}
              required
            >
              {subjects.length === 0 ? (
                <option value="">No subjects found for this class</option>
              ) : (
                <>
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>
                      {subject.code} - {subject.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>

        {(isHod || canExport) && (
          <div className="mt-4">
            <div className="flex flex-wrap items-center gap-3">
              {isHod && (
                <button
                  type="button"
                  onClick={() => setShowSubjectManager(!showSubjectManager)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-200 transition-colors font-semibold"
                >
                  {showSubjectManager ? 'Hide Subject Manager' : 'Manage Subjects'}
                </button>
              )}

              {canExport && (
                <button
                  type="button"
                  id="attendance-export-btn"
                  onClick={() => navigate('/attendance/export')}
                  className="px-4 py-2 bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-lg hover:bg-emerald-200 transition-colors flex items-center gap-2 font-semibold"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export
                </button>
              )}
            </div>

            {isHod && showSubjectManager && (
              <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-slate-50">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Add / Remove Subjects</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <input
                    type="text"
                    value={subjectForm.code}
                    onChange={(e) => setSubjectForm({ ...subjectForm, code: e.target.value })}
                    placeholder="Subject code"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                  <input
                    type="text"
                    value={subjectForm.name}
                    onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })}
                    placeholder="Subject name"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleAddSubject}
                    disabled={subjectActionLoading}
                   className="px-4 py-2 bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-lg hover:bg-emerald-200 disabled:opacity-40 font-semibold transition-colors"
                  >
                    {subjectActionLoading ? 'Saving...' : 'Add Subject'}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <select
                    value={subjectToRemove}
                    onChange={(e) => setSubjectToRemove(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="">Select subject to remove</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.code} - {subject.name}
                      </option>
                    ))}
                  </select>
                  <div className="md:col-span-2">
                    <button
                      type="button"
                      onClick={handleRemoveSubject}
                      disabled={subjectActionLoading}
                      className="px-4 py-2 bg-rose-100 text-rose-600 border border-rose-300 rounded-lg hover:bg-rose-200 disabled:opacity-40 font-semibold transition-colors"
                    >
                      {subjectActionLoading ? 'Removing...' : 'Remove Subject'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Read-only banner for past dates */}
      {!isEditable && students.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6 flex items-center gap-3">
          <svg className="w-6 h-6 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-semibold text-amber-800">View Only</p>
            <p className="text-sm text-amber-700">Attendance for past dates cannot be modified. You can only mark or update attendance for today's date.</p>
          </div>
        </div>
      )}

      {/* Subject Info with Update Mode Indicator */}
      {students.length > 0 && subjectName && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Subject</p>
              <p className="text-lg font-bold text-blue-900">{subjectName}</p>
            </div>
            {isUpdateMode && isEditable && (
              <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                Update Mode
              </span>
            )}
          </div>
        </div>
      )}

      {/* Statistics */}
      {students.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-600 text-sm font-medium">Present</p>
            <p className="text-2xl font-bold text-green-700">{stats.present}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 text-sm font-medium">Absent</p>
            <p className="text-2xl font-bold text-red-700">{stats.absent}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-600 text-sm font-medium">Late</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.late}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-600 text-sm font-medium">Excused</p>
            <p className="text-2xl font-bold text-blue-700">{stats.excused}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600 text-sm font-medium">Not Marked</p>
            <p className="text-2xl font-bold text-gray-700">{stats.notMarked}</p>
          </div>
        </div>
      )}

      {/* Actions */}
      {students.length > 0 && (
        <div className="hidden md:block bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={openDesktopAttendance}
                disabled={!isEditable || filteredStudents.length === 0}
                className={`px-4 py-2 rounded-lg border transition-colors font-semibold ${isEditable && filteredStudents.length > 0 ? 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'}`}
              >
                Start Attendance
              </button>
              <button
                onClick={markAllPresent}
                disabled={!isEditable}
                className={`px-4 py-2 rounded-lg border transition-colors font-semibold ${isEditable ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'}`}
              >
                Mark All Present
              </button>
              <button
                onClick={markAllAbsent}
                disabled={!isEditable}
                className={`px-4 py-2 rounded-lg border transition-colors font-semibold ${isEditable ? 'bg-red-100 text-red-600 border-red-300 hover:bg-red-200' : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'}`}
              >
                Mark All Absent
              </button>
            </div>

            <input
              type="text"
              placeholder="Search by name, USN, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {students.length > 0 && (
        <div className="md:hidden mb-6 space-y-4">
          {!mobileAttendanceStarted ? (
             <div className="bg-white rounded-lg shadow-md p-4 space-y-4">
              {/* Mobile search bar */}
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name or USN..."
                  value={mobileSearchQuery}
                  onChange={(e) => setMobileSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {mobileSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setMobileSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {isEditable && (
                <button
                  type="button"
                  onClick={startMobileAttendance}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Start Attendance
                </button>
              )}

              <div className="space-y-3">
                {(() => {
                  const lower = mobileSearchQuery.trim().toLowerCase();
                  // Tag each record with its original index, then sort matches to top
                  const tagged = students.map((record, index) => ({
                    record,
                    index,
                    isMatch: lower !== '' && (
                      String(record.student?.name || '').toLowerCase().includes(lower) ||
                      String(record.student?.usn || '').toLowerCase().includes(lower)
                    )
                  }));
                  const visible = lower ? tagged.filter(t => t.isMatch) : tagged;
                  return visible.map(({ record, index }) => {
                    const currentStatus = attendanceData[record.studentId]?.status || record.status || 'NOT_MARKED';
                    return (
                      <button
                        key={record.id}
                        type="button"
                        onClick={() => openMobileStudent(index)}
                        className="w-full text-left rounded-lg p-4 transition-colors active:bg-gray-100 border border-gray-200 bg-gray-50"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{record.student.name}</p>
                            <p className="text-sm text-gray-500">{record.student.usn}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap shrink-0 ${getStatusColor(currentStatus)}`}>
                            {currentStatus.replace('_', ' ')}
                          </span>
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>

              {isEditable && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`w-full px-4 py-3 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-400 ${
                    isUpdateMode
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  {loading ? 'Saving...' : isUpdateMode ? 'Update Attendance' : 'Submit Attendance'}
                </button>
              )}
            </div>
          ) : null}
        </div>
      )}

      {mobileAttendanceStarted && (
        <div className="md:hidden fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={handleMobileBackToList}></div>
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-4">
            <button
              type="button"
              onClick={handleMobileBackToList}
              className="absolute top-4 right-4 text-slate-500 hover:text-slate-700"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {mobileCurrentStudent ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 pr-8">
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wide">Student {mobileStudentIndex + 1} of {students.length}</p>
                    <h2 className="text-xl font-bold text-gray-900 mt-1">{mobileCurrentStudent.student.name}</h2>
                    <p className="text-sm text-gray-600">{mobileCurrentStudent.student.usn}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (mobileStudentIndex === 0) {
                        handleMobileBackToList();
                      } else {
                        setMobileStudentIndex(prev => prev - 1);
                      }
                    }}
                    className="shrink-0 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
                  >
                    {mobileStudentIndex === 0 ? 'Back' : '← Prev'}
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { status: 'PRESENT',  bg: 'bg-emerald-500', ring: 'ring-emerald-600', dim: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
                    { status: 'ABSENT',   bg: 'bg-red-500',     ring: 'ring-red-600',     dim: 'bg-red-100 text-red-700 border-red-200' },
                    { status: 'LATE',     bg: 'bg-amber-500',   ring: 'ring-amber-600',   dim: 'bg-amber-100 text-amber-700 border-amber-200' },
                    { status: 'EXCUSED',  bg: 'bg-blue-500',    ring: 'ring-blue-600',    dim: 'bg-blue-100 text-blue-700 border-blue-200' },
                  ].map(({ status, bg, ring, dim }) => {
                    const isSelected = mobileCurrentStatus === status;
                    return (
                      <button
                        key={status}
                        type="button"
                        disabled={!isEditable}
                        onClick={() => {
                          if (!isEditable) return;
                          handleStatusChange(mobileCurrentStudent.studentId, status);
                          // Auto-advance to next student
                          setTimeout(() => {
                            if (mobileStudentIndex >= students.length - 1) {
                              return;
                            }
                            setMobileStudentIndex(prev => prev + 1);
                          }, 150);
                        }}
                        className={`py-4 text-sm font-bold rounded-xl border-2 transition-all ${
                          !isEditable
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200'
                            : isSelected
                              ? `${bg} text-white border-transparent ring-4 ${ring}/30 scale-[1.03] shadow-md`
                              : `${dim} border-transparent hover:scale-[1.01]`
                        }`}
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                  <input
                    type="text"
                    value={attendanceData[mobileCurrentStudent.studentId]?.remarks || ''}
                    onChange={(e) => handleRemarksChange(mobileCurrentStudent.studentId, e.target.value)}
                    placeholder="Optional remarks..."
                    disabled={!isEditable}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${!isEditable ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleMobileBackToList}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50"
                  >
                    Back to List
                  </button>
                  {showMobileSubmit && (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className={`w-full px-4 py-3 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-400 ${
                        isUpdateMode
                          ? 'bg-orange-600 hover:bg-orange-700'
                          : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    >
                      {loading ? 'Saving...' : isUpdateMode ? 'Update Attendance' : 'Submit Attendance'}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">No students available for this view.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Student List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : students.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-500 text-lg">
            {department && semester
              ? 'No students found for this class'
              : 'Please select department and semester to load students'}
          </p>
        </div>
      ) : (
        <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    USN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remarks
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((record, index) => {
                  const currentStatus = attendanceData[record.studentId]?.status || record.status;
                  return (
                    <tr
                      key={record.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDesktopAttendanceAt(index)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {record.student.usn}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.student.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {[
                            { status: 'PRESENT', bg: 'bg-emerald-500', ring: 'ring-emerald-600', dim: 'bg-emerald-100 text-emerald-700' },
                            { status: 'ABSENT',  bg: 'bg-red-500',     ring: 'ring-red-600',     dim: 'bg-red-100 text-red-700' },
                            { status: 'LATE',    bg: 'bg-amber-500',   ring: 'ring-amber-600',   dim: 'bg-amber-100 text-amber-700' },
                            { status: 'EXCUSED', bg: 'bg-blue-500',    ring: 'ring-blue-600',    dim: 'bg-blue-100 text-blue-700' },
                          ].map(({ status, bg, ring, dim }) => {
                            const isSelected = currentStatus === status;
                            return (
                              <button
                                key={status}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (isEditable) {
                                    handleStatusChange(record.studentId, status);
                                  }
                                }}
                                disabled={!isEditable}
                                className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                                  !isEditable
                                    ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                                    : isSelected
                                      ? `${bg} text-white ring-2 ${ring}/40 shadow-sm`
                                      : `${dim} hover:opacity-90`
                                }`}
                              >
                                {status}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="text"
                          value={attendanceData[record.studentId]?.remarks || ''}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(e) => handleRemarksChange(record.studentId, e.target.value)}
                          placeholder={isEditable ? 'Optional remarks...' : ''}
                          disabled={!isEditable}
                          readOnly={!isEditable}
                          className={`w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 ${!isEditable ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {filteredStudents.length} of {students.length} students
            </p>
            {isEditable ? (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-6 py-2 text-white rounded-lg transition-colors disabled:bg-gray-400 ${
                  isUpdateMode 
                    ? 'bg-orange-600 hover:bg-orange-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isUpdateMode ? 'Update Attendance' : 'Submit Attendance'}
              </button>
            ) : (
              <span className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg text-sm font-medium">
                View Only — Past Date
              </span>
            )}
          </div>
        </div>
      )}

      {desktopAttendanceOpen && (
        <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={closeDesktopAttendance}></div>
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
            <button
              type="button"
              onClick={closeDesktopAttendance}
              className="absolute top-5 right-5 text-slate-500 hover:text-slate-700"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {desktopCurrentStudent ? (
              <div className="space-y-6">
                <div className="flex items-start justify-between gap-4 pr-12">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Student {desktopStudentIndex + 1} of {filteredStudents.length}
                    </p>
                    <h2 className="text-2xl font-bold text-slate-900 mt-2">
                      {desktopCurrentStudent.student.name}
                    </h2>
                    <p className="text-sm text-slate-600 mt-1">
                      {desktopCurrentStudent.student.usn}
                    </p>
                    <p className="text-sm text-slate-500">
                      {desktopCurrentStudent.student.collegeEmail || 'No email on record'}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(desktopCurrentStatus)}`}>
                    {desktopCurrentStatus.replace('_', ' ')}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { status: 'PRESENT',  bg: 'bg-emerald-500', ring: 'ring-emerald-600', dim: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
                    { status: 'ABSENT',   bg: 'bg-red-500',     ring: 'ring-red-600',     dim: 'bg-red-100 text-red-700 border-red-200' },
                    { status: 'LATE',     bg: 'bg-amber-500',   ring: 'ring-amber-600',   dim: 'bg-amber-100 text-amber-700 border-amber-200' },
                    { status: 'EXCUSED',  bg: 'bg-blue-500',    ring: 'ring-blue-600',    dim: 'bg-blue-100 text-blue-700 border-blue-200' },
                  ].map(({ status, bg, ring, dim }) => {
                    const isSelected = desktopCurrentStatus === status;
                    return (
                      <button
                        key={status}
                        type="button"
                        disabled={!isEditable}
                        onClick={() => {
                          if (!isEditable) return;
                          handleStatusChange(desktopCurrentStudent.studentId, status);
                          autoAdvanceDesktop();
                        }}
                        className={`py-4 text-sm font-bold rounded-xl border-2 transition-all ${
                          !isEditable
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200'
                            : isSelected
                              ? `${bg} text-white border-transparent ring-4 ${ring}/30 scale-[1.03] shadow-md`
                              : `${dim} border-transparent hover:scale-[1.01]`
                        }`}
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={goToDesktopPrev}
                    className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50"
                    disabled={desktopStudentIndex === 0}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={showDesktopSubmit ? handleSubmit : goToDesktopNext}
                    className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50"
                    disabled={showDesktopSubmit ? loading : false}
                  >
                    {showDesktopSubmit ? (isUpdateMode ? 'Update Attendance' : 'Submit Attendance') : 'Next'}
                  </button>
                </div>

                <div className="text-xs text-slate-400">
                  Shortcuts: Left = Absent, Right = Present, Up = Late, Down = Excused, Cmd/Ctrl+Left = Previous, Cmd/Ctrl+Right = Next.
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-500">No students available for this view.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkAttendance;
